/**
 * Horizontal pill bar for navigating between managed-agent sessions.
 * Renders above ChatMessageList in SubAgentChatView. Each pill shows a
 * short session label (sequential number + relative time). The active
 * session is highlighted with the agent's accent color.
 */

import React, { useRef, useEffect } from "react";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { cn } from "@/lib/utils";

export interface SessionPillItem {
  id: string;
  label: string;
  /** ISO timestamp — shown as relative time */
  createdAt: string;
  /** Semantic category of the session's output (`image-ad`, `ugc-portrait`,
   *  `ugc-reference`, `video-ad`, `ugc-video`). When present, the pill
   *  renders a friendly plural label ("Image Ads", "Portraits") instead of
   *  the bare sequential index. Null on legacy sessions with no tag. */
  assetKind?: string | null;
  /** Synthetic placeholder for a brand-new chat the user has opened but not
   *  yet sent a message into. The real session id doesn't exist until the
   *  first turn lands, so the bar shows this pill in the meantime to confirm
   *  the "+" click took effect. Excluded from the sequential index. */
  isDraft?: boolean;
}

/** Map a session's `assetKind` tag to the short plural label shown on its pill.
 *  Kept inside the component so every caller of the pill bar gets the same
 *  vocabulary without duplicating the map. */
function assetKindLabel(kind: string | null | undefined): string | null {
  switch (kind) {
    case "image-ad":
      return "Image Ads";
    case "ugc-portrait":
      return "Portraits";
    case "ugc-reference":
      return "UGC Refs";
    case "video-ad":
      return "Videos";
    case "ugc-video":
      return "UGC Videos";
    default:
      return null;
  }
}

interface SessionPillBarProps {
  sessions: SessionPillItem[];
  activeSessionId: string | null;
  onSelect: (sessionId: string) => void;
  accentColor: string;
  /** Session IDs whose agent is currently running mid-session. Pills in this
   *  set render a pulsing dot + accent-tinted ring so the user can spot live
   *  work even while viewing a different (historical) session. */
  runningSessionIds?: readonly string[];
}

function relativeTime(iso: string): string {
  const diff = Date.now() - new Date(iso).getTime();
  const mins = Math.floor(diff / 60_000);
  if (mins < 1) return "now";
  if (mins < 60) return `${mins}m`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h`;
  const days = Math.floor(hrs / 24);
  return `${days}d`;
}

export function SessionPillBar({
  sessions,
  activeSessionId,
  onSelect,
  accentColor,
  runningSessionIds,
}: SessionPillBarProps) {
  const scrollRef = useRef<HTMLDivElement>(null);
  const activePillRef = useRef<HTMLButtonElement>(null);
  const runningSet = React.useMemo(
    () => new Set(runningSessionIds ?? []),
    [runningSessionIds],
  );

  // Scroll active pill into view when it changes
  useEffect(() => {
    activePillRef.current?.scrollIntoView({ behavior: "smooth", block: "nearest", inline: "center" });
  }, [activeSessionId]);

  if (sessions.length <= 1) return null;

  const scroll = (dir: "left" | "right") => {
    scrollRef.current?.scrollBy({ left: dir === "left" ? -120 : 120, behavior: "smooth" });
  };

  return (
    <div className="shrink-0 flex items-center gap-1 px-3 py-1.5 border-b border-border/10 bg-background/20">
      <button
        onClick={() => scroll("left")}
        className="shrink-0 w-5 h-5 flex items-center justify-center rounded hover:bg-muted/40 text-muted-foreground/50"
      >
        <ChevronLeft className="w-3 h-3" />
      </button>

      <div
        ref={scrollRef}
        className="flex-1 flex items-center gap-1 overflow-x-auto hidden-scrollbar"
      >
        {(() => {
          // The draft pill (if present) sits at index 0 but should not count
          // toward the sequential numbering of real sessions — otherwise
          // adding a draft would shift every "#N" label by one.
          const realCount = sessions.filter((s) => !s.isDraft).length;
          let realSeen = 0;
          return sessions.map((s) => {
            const isActive = s.id === activeSessionId;
            const isRunning = runningSet.has(s.id);

            if (s.isDraft) {
              return (
                <button
                  key={s.id}
                  ref={isActive ? activePillRef : undefined}
                  onClick={() => onSelect(s.id)}
                  className={cn(
                    "relative shrink-0 flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-medium transition-all",
                    isActive
                      ? "text-white shadow-sm"
                      : "text-muted-foreground/70 hover:text-foreground/80 bg-muted/20 hover:bg-muted/40 border border-dashed border-border/40",
                  )}
                  style={isActive ? { backgroundColor: accentColor } : undefined}
                  title="New chat"
                >
                  <span className="relative font-semibold">New</span>
                </button>
              );
            }

            realSeen += 1;
            const kindLabel = assetKindLabel(s.assetKind);
            const primary = kindLabel ?? `#${realCount - realSeen + 1}`;
            const style: React.CSSProperties = {};
            if (isActive) style.backgroundColor = accentColor;
            if (isRunning && !isActive) style.boxShadow = `0 0 0 1.5px ${accentColor}`;
            return (
              <button
                key={s.id}
                ref={isActive ? activePillRef : undefined}
                onClick={() => onSelect(s.id)}
                className={cn(
                  "relative shrink-0 flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-medium transition-all",
                  isActive
                    ? "text-white shadow-sm"
                    : "text-muted-foreground/70 hover:text-foreground/80 bg-muted/20 hover:bg-muted/40 border border-border/20",
                )}
                style={Object.keys(style).length > 0 ? style : undefined}
                title={`Session ${realCount - realSeen + 1}${kindLabel ? ` — ${kindLabel}` : ""}${isRunning ? " — running" : ""} — ${new Date(s.createdAt).toLocaleString()}`}
              >
                {isRunning && (
                  <>
                    <span
                      aria-hidden
                      className="absolute -inset-0.5 rounded-full animate-pulse pointer-events-none"
                      style={{ boxShadow: `0 0 8px 1px ${accentColor}` }}
                    />
                    <span
                      aria-hidden
                      className="relative w-1.5 h-1.5 rounded-full animate-pulse"
                      style={{ backgroundColor: isActive ? "#ffffff" : accentColor }}
                    />
                  </>
                )}
                <span className="relative font-semibold">{primary}</span>
                <span className="relative opacity-70">{relativeTime(s.createdAt)}</span>
              </button>
            );
          });
        })()}
      </div>

      <button
        onClick={() => scroll("right")}
        className="shrink-0 w-5 h-5 flex items-center justify-center rounded hover:bg-muted/40 text-muted-foreground/50"
      >
        <ChevronRight className="w-3 h-3" />
      </button>
    </div>
  );
}
